import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { fetchUserSubscriptions, reverseUserSubscription, fetchUserDetail } from '../../../../../redux/slice/allUsersSlice';
import CustomTable from '../../../../../components/table/CustomTable';
import { formatMomentDate } from '../../../../../utils/dateHelper';
import { useToast } from '../../../../../context/TostContext';
import ConfirmModal from '../../../../../components/withModal/ConfirmModal';
import StatusChip from '../../../../../components/statusChip/StatusChip';
import SubscriptionDetails from './SubscriptionDetails';
import Entitlements from './Entitlements';

const getStatusBg = (status) => {
    const lowerStatus = status?.toLowerCase();
    switch (lowerStatus) {
        case "active":
            return "success";
        case "trialing":
        case "initialized":
            return "blue";
        case "cancelled":
            return "warning";
        case "expired":
            return "secondary";
        case "paid":
            return "success";
        case "revoked":
        case "failed":
            return "danger";
        case "pending":
            return "warning";
        default:
            return "";
    }
};

const parsePaymentDetails = (details, group) => {
    if (!details) return "-";

    const method = details.payment_method || details;

    if (group === "upi" && method.upi) {
        return `UPI ID: ${method.upi.upi_id || "N/A"}`;
    }
    if (group === "card" && method.card) {
        const { card_bank_name, card_network, card_type, card_number } = method.card;
        const typeFormatted = card_type ? card_type.replace('_', ' ') : '';
        return [card_bank_name, card_network, typeFormatted, `ending in ${card_number}`].filter(Boolean).join(" ").toUpperCase();
    }
    return group ? group.toUpperCase() : "-";
};

const Subscriptions = ({ userDetail }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const [searchParams, setSearchParams] = useSearchParams();

    const subscriptionId = searchParams.get('subscription-id');

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [showReversePopup, setShowReversePopup] = useState(false);
    const [subscriptionToReverse, setSubscriptionToReverse] = useState(null);

    const {
        userSubscriptions,
        userSubscriptionsLoading,
        userSubscriptionsTotal
    } = useSelector(state => state.allUsers);

    useEffect(() => {
        if (userDetail?.id) {
            dispatch(fetchUserSubscriptions({ id: userDetail.id, page, limit }));
        }
    }, [dispatch, userDetail?.id, page, limit]);

    const handleReverseConfirm = () => {
        if (!subscriptionToReverse) return;
        dispatch(reverseUserSubscription({ subscription_id: subscriptionToReverse.id }))
            .unwrap()
            .then((res) => {
                toast.success(res?.message || "Subscription reversed successfully");
                setShowReversePopup(false);
                setSubscriptionToReverse(null);
                if (userDetail?.id) {
                    dispatch(fetchUserDetail(userDetail.id));
                    dispatch(fetchUserSubscriptions({ id: userDetail.id, page, limit }));
                }
            })
            .catch((err) => {
                toast.error(err?.message || "Failed to reverse subscription");
                setShowReversePopup(false);
                setSubscriptionToReverse(null);
            });
    };

    const handleViewSubscription = (id) => {
        const updated = new URLSearchParams(searchParams);
        updated.set('subscription-id', id);
        setSearchParams(updated);
    };

    const actionList = [
        {
            title: "View",

            handler: (row) => handleViewSubscription(row?.id)
        },
        {
            title: "Revoke Plan",
            isVisible: (row) => row.status === 'active' && row.created_by_type === 'admin',
            handler: (row) => {
                setSubscriptionToReverse(row);
                setShowReversePopup(true);
            }
        }
    ];
    const columns = [

        {
            header: 'Plan',
            cell: (_, item) => (
                <>
                    <p className="fw-medium text-nowrap">{item.plan?.name || "-"}</p>
                    <small className="text-muted text-capitalize text-nowrap">{item.plan?.interval || "-"}</small>
                </>
            )
        },
        {
            header: 'Source',
            cell: (_, item) => <div className="text-capitalize text-nowrap">{item.source || "-"}</div>
        },
        {
            header: 'Is Trial',
            cell: (_, item) => (
                item.is_trial ? <StatusChip bg="blue" pill title="Yes" /> : <StatusChip bg="secondary" pill title="No" />
            )
        },
        {
            header: 'Start Date',
            cell: (_, item) => <div className="text-nowrap">{item.start_date ? formatMomentDate(item.start_date, "DD-MM-YYYY hh:mm A") : "-"}</div>
        },
        {
            header: 'Trial End Date',
            cell: (_, item) => <div className="text-nowrap">{item.trial_end_date ? formatMomentDate(item.trial_end_date, "DD-MM-YYYY hh:mm A") : "-"}</div>
        },
        {
            header: 'End Date',
            cell: (_, item) => <div className="text-nowrap">{item.end_date ? formatMomentDate(item.end_date, "DD-MM-YYYY hh:mm A") : "-"}</div>
        },
        {
            header: 'Total Paid Amount',
            cell: (_, item) => {
                const amount = item.total_amount_paid;
                return typeof amount === 'number' ? <div className="text-nowrap fw-semibold">₹{parseFloat(amount).toLocaleString()}</div> : "-";
            }
        },
        {
            header: 'Total Transactions',
            cell: (_, item) => {
                const count = item.total_payment_transactions;
                return typeof count === 'number' ? <div className="text-nowrap">{count}</div> : "-";
            }
        },
        {
            header: 'Status',
            cell: (_, item) => (
                item.status ? (
                    <StatusChip
                        bg={getStatusBg(item.status)}
                        pill
                        title={item.status}
                        className="text-capitalize"
                    />
                ) : (
                    "-"
                )
            )
        },
        {
            header: 'Auto Renewal',
            cell: (_, item) => (
                item.auto_renewal ? (
                    <StatusChip bg="chip-green" pill title="Enabled" />
                ) : (
                    <StatusChip bg="chip-secondary" pill title="Disabled" />
                )
            )
        },
        {
            header: 'Created By',
            cell: (_, item) => {
                if (item.created_by_type === 'admin' && item.creator_admin) {
                    return <div className="text-nowrap">Admin: {item.creator_admin.name}</div>;
                }
                if (item.created_by_type === 'user' && item.creator_user) {
                    return <div className="text-nowrap">User: {item.creator_user.first_name} {item.creator_user.last_name}</div>;
                }
                return "-";
            }
        },
        {
            header: 'Revoked By',
            cell: (_, item) => {
                if (item.status === 'revoked' && item.revoked_by) {
                    return <div className="text-nowrap text-danger fw-medium">Admin: {item.revoked_by.name || 'Admin'}</div>;
                }
                return "-";
            }
        },
        {
            header: 'Revoked At',
            cell: (_, item) => (
                item.status === 'revoked' && item.revoked_at ? (
                    <div className="text-nowrap">{formatMomentDate(item.revoked_at, "DD-MM-YYYY hh:mm A")}</div>
                ) : "-"
            )
        },
        {
            header: 'Last Payable Amount',
            cell: (_, item) => {
                const amount = item.payment_transaction?.payable_amount;
                return amount ? <div className="text-nowrap fw-semibold">₹{parseFloat(amount).toLocaleString()}</div> : "-";
            }
        },
        {
            header: 'Last Payment Method',
            cell: (_, item) => {
                const group = item.payment_transaction?.payment_group || item.payment_transaction?.payment_method?.type;
                return <div className="text-nowrap text-capitalize">{group || "-"}</div>;
            }
        },
        {
            header: 'Last Method Details',
            cell: (_, item) => {
                const tx = item.payment_transaction;
                if (!tx || (!tx.payment_details && !tx.payment_method)) return "-";
                const details = tx.payment_details || tx.payment_method;
                return <div className="text-nowrap small">{parsePaymentDetails(details, tx.payment_group)}</div>;
            }
        },
        {
            header: 'Last Payment ID',
            cell: (_, item) => {
                const tx = item.payment_transaction;
                if (!tx) return "-";
                const id = tx.payment_id || tx.cf_payment_id;
                return <div className="text-nowrap">{id || "-"}</div>;
            }
        },
        {
            header: 'Last Payment Status',
            cell: (_, item) => {
                const status = item.payment_transaction?.status;
                return status ? (
                    <StatusChip
                        bg={getStatusBg(status)}
                        pill
                        title={status}
                        className="text-capitalize"
                    />
                ) : "-";
            }
        },
        {
            header: 'Last Paid At',
            cell: (_, item) => {
                const time = item.payment_transaction?.paid_at || item.payment_transaction?.payment_time;
                return time ? <div className="text-nowrap">{formatMomentDate(time, "DD-MM-YYYY hh:mm A")}</div> : "-";
            }
        },
        {
            header: 'Last Error Details',
            cell: (_, item) => {
                const err = item.payment_transaction?.error_details;
                if (!err) return "-";
                return <div className="text-nowrap small text-danger" style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }} title={typeof err === 'string' ? err : JSON.stringify(err)}>{typeof err === 'string' ? err : "See error object"}</div>;
            }
        }
    ];

    const totalPages = Math.ceil((userSubscriptionsTotal || 0) / limit) || 1;

    if (subscriptionId) {
        return <SubscriptionDetails subscriptionId={subscriptionId} userDetail={userDetail} />;
    }

    return (
        <div className="position-relative h-100">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <h6 className="fw-bold mb-0" style={{ fontSize: '18px' }}>Subscriptions</h6>
            </div>

            <div className="border rounded-3 overflow-hidden bg-white">
                <CustomTable
                    columns={columns}
                    data={userSubscriptions || []}
                    loading={userSubscriptionsLoading}
                    actionList={actionList}
                    emptyMessage="No subscriptions found."
                    pagination={{
                        currentPage: page,
                        totalPages,
                        total: userSubscriptionsTotal,
                        onPageChange: (newPage) => setPage(newPage),
                        pageSize: limit,
                        setPageSize: (newLimit) => {
                            setLimit(newLimit);
                            setPage(1);
                        },
                        setCurrentPage: (newPage) => setPage(newPage)
                    }}
                />
            </div>

            <Entitlements userDetail={userDetail} />

            {showReversePopup && (
                <ConfirmModal
                    isOpen={showReversePopup}
                    onClose={() => {
                        setShowReversePopup(false);
                        setSubscriptionToReverse(null);
                    }}
                    title="Revoke Subscription Plan"
                    message="Are you sure you want to revoke this admin-assigned subscription? Premium features will be deactivated immediately."
                    confirmText="Revoke"
                    cancelText="Cancel"
                    onConfirm={handleReverseConfirm}
                    className="confirm-modal"
                />
            )}
        </div>
    );
};

export default Subscriptions;
